import { useEnrichOffersWithChannelIds } from '@hooks/useEnrichOffersWithChannelIds';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import {
	Box,
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Pagination,
	Select,
	SelectChangeEvent,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSpecialistOffersStore } from '../services';
import { getOffers } from '../services/services';
import { OfferStatus } from '../types';
import { OfferCard } from './OfferCard';

// Extend the offer type to include channelId for typescript
type ExtendedOffer = {
	request_id?: string;
	farmer_id?: string;
	specialist_id?: string;
	channelId?: string;
};

interface OffersListProps {
	status?: OfferStatus;
	onStatusChange?: (status: OfferStatus) => void;
}

export const OffersList: React.FC<OffersListProps> = ({ status = 'active', onStatusChange }) => {
	const { t } = useTranslation();

	const { navigateWithLanguage } = useLanguageNavigate();
	const { offers, pagination, isLoading, error, setStatus, setOffers, setIsLoading, setError } =
		useSpecialistOffersStore();
	const { enrichOffersWithChannelIds } = useEnrichOffersWithChannelIds();

	const [page, setPage] = useState(1);

	// Fetch offers when pagination changes
	useEffect(() => {
		const fetchOffersWithPagination = async () => {
			try {
				setIsLoading(true);
				const offersResponse = await getOffers(status, page);
				setOffers(offersResponse);

				// Once offers are loaded and set in the store, enrich them with channel IDs
				if (offersResponse.data.length > 0) {
					// Prepare offers for enrichment by ensuring they have the required properties
					const preparedOffers = offersResponse.data.map((offer) => ({
						...offer,
						farmer_id: offer.farmer?.id,
						specialist_id: offer.specialist_id,
					}));

					// Use our hook to fetch channel IDs
					await enrichOffersWithChannelIds(preparedOffers, (enrichedOffers) => {
						// Update the store with the enriched offers
						setOffers({
							...offersResponse,
							data: enrichedOffers,
						});
					});
				}
			} catch (error) {
				setError(error instanceof Error ? error.message : 'Failed to fetch offers');
			} finally {
				setIsLoading(false);
			}
		};

		fetchOffersWithPagination();
	}, [page, status, setOffers, setIsLoading, setError, enrichOffersWithChannelIds]);

	const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	};

	const handleStatusChange = (event: SelectChangeEvent) => {
		const newStatus = event.target.value as OfferStatus;
		setStatus(newStatus);
		// Reset pagination when status changes
		setPage(1);
		if (onStatusChange) {
			onStatusChange(newStatus);
		}
	};

	const handleSendOffer = (offerId: string) => {
		navigateWithLanguage(`/specialist/express/offers/${offerId}`);
	};

	const handleEditOffer = (offerId: string) => {
		navigateWithLanguage(`/specialist/express/offers/${offerId}`);
	};

	const handleChat = (request_id: string) => {
		// Find the offer with this ID
		const offer = offers.find((o) => o.request_id === request_id);
		if (offer && 'channelId' in offer && offer.channelId) {
			navigateWithLanguage(`/specialist/chats?channelId=${offer.channelId}`);
		} else {
			console.log('No channel ID available for this offer');
		}
	};

	const handleViewDetails = (offerId: string) => {
		navigateWithLanguage(`/specialist/express/offers/${offerId}`);
	};

	// Status filter dropdown - extracted as a separate component to be reused
	const StatusFilter = () => (
		<Box className="flex justify-between items-center mb-6">
			<Typography variant="h5" className="font-medium">
				{status === 'active'
					? t('ongoingOffers', 'Ongoing Offers')
					: status === 'archived'
						? t('archivedOffers', 'Archived Offers')
						: t('completedOffers', 'Completed Offers')}
			</Typography>

			<FormControl variant="outlined" size="small" className="min-w-[150px]">
				<InputLabel id="status-select-label">{t('status')}</InputLabel>
				<Select
					labelId="status-select-label"
					value={status}
					onChange={handleStatusChange}
					label={t('status')}
				>
					<MenuItem value="active">{t('ongoing', 'Ongoing')}</MenuItem>
					<MenuItem value="archived">{t('archived', 'Archived')}</MenuItem>
					<MenuItem value="completed">{t('completed')}</MenuItem>
				</Select>
			</FormControl>
		</Box>
	);

	if (isLoading) {
		return (
			<Box className="py-4">
				<StatusFilter />
				<Box className="flex justify-center items-center h-64">
					<CircularProgress />
				</Box>
			</Box>
		);
	}

	if (error) {
		return (
			<Box className="py-4">
				<StatusFilter />
				<Box className="flex justify-center items-center h-64">
					<Typography color="error">{error}</Typography>
				</Box>
			</Box>
		);
	}

	if (offers.length === 0) {
		return (
			<Box className="py-4">
				<StatusFilter />
				<Box className="flex flex-col items-center justify-center h-64">
					<Typography variant="body1" className="text-gray-600">
						{status === 'active'
							? t('noOffersToDisplay')
							: status === 'archived'
								? t('noArchivedOffersYet')
								: t('noCompletedOffersYet')}
					</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box className="py-4">
			<StatusFilter />

			<Grid container spacing={3}>
				{offers.map((offer) => (
					<Grid item xs={12} sm={6} md={6} key={offer.id}>
						<OfferCard
							offer={offer}
							onSendOffer={handleSendOffer}
							onEditOffer={handleEditOffer}
							onChat={handleChat}
							onViewDetails={handleViewDetails}
							status={status}
						/>
					</Grid>
				))}
			</Grid>

			{pagination && pagination.total_pages > 1 && (
				<Box className="flex justify-center mt-6">
					<Pagination
						count={pagination.total_pages}
						page={page}
						onChange={handlePageChange}
						color="primary"
						shape="rounded"
					/>
				</Box>
			)}
		</Box>
	);
};
